import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/de'; // Deutsche Lokalisierung für dayjs laden
import { toast } from 'react-toastify';

// Importiere die Unterkomponenten
import TournamentHeader from '../components/turniere/TournamentHeader';
import TournamentInfoBox from '../components/turniere/TournamentInfoBox';
import TournamentRanking from '../components/turniere/TournamentRanking';
import TournamentActions from '../components/turniere/TournamentActions';
import ParticipantManager from '../components/turniere/ParticipantManager';

// Importiere die Modal-Komponenten
import TournamentEdit from '../components/turniere/TournamentEdit';
import PartificantAdd from '../components/turniere/partificantAdd';
import TournamentDelete from '../components/turniere/tournamentDelete';
import ParticipantShow from "../components/turniere/partificantShow";
import TournamentClose from '../components/turniere/tournamentClose';
import ConfirmModal from '../components/turniere/ConfirmModal';
import TournamentMessages from '../components/turniere/TournamentMessages';
import PublishConfirmModal from '../components/turniere/PublishConfirmModal'; // Modal für Veröffentlichung

// Hauptkomponente für die Turnierdetailansicht
function Tournaments() {
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true); // Haupt-Ladezustand
    const [participants, setParticipants] = useState([]);
    const [participantsConfirmed, setParticipantsConfirmed] = useState([]);
    const [participantsWait, setParticipantsWait] = useState([]);
    const [participantsCanceled, setParticipantsCanceled] = useState([]);
    const [inquiry, setInquiry] = useState([]);

    // State für Modals
    const [editTournamentModal, setEditTournamentModal] = useState(false);
    const [addPartificantModal, setAddPartificantModal] = useState(false);
    const [deleteTournamentModal, setDeleteTournamentModal] = useState(false);
    const [showParticipantModal, setShowParticipantModal] = useState(false);
    const [showTournamentCloseModal, setShowTournamentCloseModal] = useState(false);
    const [showTournamentMessagesModal, setShowTournamentMessagesModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false); // Für Teilnehmer-Statusänderungen
    const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false); // Für Turnier-Veröffentlichung

    // State für Datenübergabe an Modals
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [confirmModalTopic, setConfirmModalTopic] = useState(0); // Welcher Bestätigungsdialog (Teilnehmer)

    const { id } = useParams(); // Turnier-ID aus der URL

    // --- DATENABRUF ---
    const fetchParticipants = useCallback((tournamentId) => {
        axios.get(`/tournament/${tournamentId}/participants`)
            .then((response) => {
                const data = response.data.data || [];
                setParticipants(data);
                setParticipantsConfirmed(data.filter(p => p.status === 1));
                setParticipantsCanceled(data.filter(p => p.status === 3));
                setParticipantsWait(data.filter(p => p.status === 2));
                setInquiry(data.filter(p => !p.status)); // Status 0 oder fehlend = Anfrage
            })
            .catch((error) => {
                console.error("Fehler beim Laden der Teilnehmer:", error);
                toast.error("Fehler beim Laden der Teilnehmerliste.");
            });
    }, []);

    const fetchTournamentData = useCallback(() => {
        setLoading(true);
        axios.get(`/tournament/${id}`)
            .then((response) => {
                setTournament(response.data);
                fetchParticipants(id); // Teilnehmer nach Turnier laden
            })
            .catch((error) => {
                console.error("Fehler beim Laden des Turniers:", error);
                toast.error("Fehler beim Laden der Turnierdetails.");
                setTournament(null);
            })
            .finally(() => {
                setLoading(false); // Ladezustand beenden
            });
    }, [id, fetchParticipants]);

    useEffect(() => {
        fetchTournamentData();
    }, [fetchTournamentData]);

    // --- HANDLER FUNKTIONEN ---
    const handleRefreshParticipants = () => {
        if (id) {
            setLoading(true);
            fetchParticipants(id);
            // Optional: Auch Turnierdaten neu laden, falls sich dort etwas ändern könnte
            // fetchTournamentData(); // Würde setLoading doppelt setzen, daher oben nur fetchParticipants
            // Besser: Eigener Loading-State für Teilnehmer oder setLoading in fetchParticipants' finally
            setLoading(false); // Provisorisch hier, um Ladeanzeige zu beenden
        }
    };

    const handleConfirmModalHide = (reload) => {
        setShowConfirmModal(false);
        if (reload) {
            handleRefreshParticipants();
        }
        setSelectedParticipant(null);
    };

    const handleParticipantUpdateAction = (participant, topic) => {
        setSelectedParticipant(participant);
        let modalTopic = topic;
        if (topic === 3 && participantsConfirmed.length >= (tournament?.teams || 0)) {
            modalTopic = 4;
        } else if (topic === 1 && participant.paid) {
            modalTopic = 2;
        }
        setConfirmModalTopic(modalTopic);
        setShowConfirmModal(true);
    };

    const handleParticipantDeleteAction = (participantId) => {
        if (!window.confirm("Soll der Teilnehmer wirklich gelöscht werden?")) return;
        setLoading(true);
        axios.delete(`/tournament/${id}/participants/${participantId}`)
            .then(() => {
                toast.success("Teilnehmer erfolgreich gelöscht.");
                handleRefreshParticipants();
            })
            .catch((error) => {
                setLoading(false);
                console.error("Fehler beim Löschen des Teilnehmers:", error);
                toast.error("Fehler beim Löschen des Teilnehmers.");
            });
    };

    const handleParticipantDownloadAction = () => {
        if (!tournament) return;
        setLoading(true);
        const sanitizedTournamentName = tournament.name.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_').trim() || 'Turnier';
        const filename = `${sanitizedTournamentName}-Teilnehmerliste.xlsx`;
        axios.get(`/tournament/${id}/participants/download`, { responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Fehler beim Herunterladen:", error);
                toast.error('Fehler beim Herunterladen der Datei.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleShowPublishConfirm = () => {
        if (!tournament) return;
        setShowPublishConfirmModal(true);
    };

    const handlePublicationAction = async (sendEmail) => {
        if (!tournament) return;
        const originalPublishedState = tournament.published;
        const originalRegistrationState = tournament.registration;
        setShowPublishConfirmModal(false);
        setLoading(true);
        try {
            // Schritt 1: Turnierstatus aktualisieren
            await axios.put(`/api/Tournaments/${id}`, { published: true, registration: true });

            if (sendEmail) {
                toast.info("Turnierstatus aktualisiert, sende Einladungen...");
                // Schritt 2: Einladungen senden (nur wenn gewünscht)
                try {
                    await axios.post(`/mail/send-invites/${id}`);
                    toast.success("Turnier veröffentlicht und Einladungen erfolgreich gesendet!");
                } catch (inviteError) {
                    // Schritt 3: Fehler beim E-Mail-Versand -> Rollback
                    console.error("Fehler beim Senden der Einladungen:", inviteError);
                    toast.error("Fehler beim Senden der Einladungen! Setze Turnierstatus zurück...");
                    try {
                        await axios.put(`/api/Tournaments/${id}`, { published: originalPublishedState, registration: originalRegistrationState });
                        toast.warn("Turnierstatus wurde aufgrund eines E-Mail-Fehlers zurückgesetzt.");
                    } catch (rollbackError) {
                        console.error("KRITISCHER FEHLER: Rollback fehlgeschlagen!", rollbackError);
                        toast.error("KRITISCHER FEHLER: Turnierstatus konnte nicht zurückgesetzt werden! Bitte manuell prüfen.");
                    }
                    // Wichtig: Hier returnen wir, damit fetchTournamentData im finally Block aufgerufen wird
                    return;
                }
            } else {
                toast.success("Turnier erfolgreich veröffentlicht (ohne E-Mail-Versand).");
            }

            fetchTournamentData(); // Endgültigen Zustand laden
        } catch (updateError) {
            // Fehler bei Schritt 1 (Turnierstatus-Update)
            console.error("Fehler beim Aktualisieren des Turnierstatus:", updateError);
            toast.error(updateError.response?.data?.message || "Fehler beim Aktualisieren des Turnierstatus.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRegistrationAction = () => {
        if (!tournament || !tournament.published) {
            toast.error('Turnier wurde noch nicht veröffentlicht.');
            return;
        }
        setLoading(true);
        const newRegistrationStatus = !tournament.registration;
        axios.put(`/api/Tournaments/${id}`, { registration: newRegistrationStatus })
            .then(() => {
                toast.success(`Anmeldung ${newRegistrationStatus ? 'geöffnet' : 'geschlossen'}.`);
                setTournament(prev => prev ? ({ ...prev, registration: newRegistrationStatus }) : null);
            })
            .catch((error) => {
                console.error("Fehler beim Ändern des Anmeldestatus:", error);
                toast.error("Fehler beim Ändern des Anmeldestatus.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleParticipantShowAction = (participant) => {
        setSelectedParticipant(participant);
        setShowParticipantModal(true);
    };

    const handleSendMessageRequest = (participantToSend) => {
        setShowParticipantModal(false);
        setSelectedParticipant(participantToSend);
        setShowTournamentMessagesModal(true);
    };

    // --- RENDERING ---
    if (loading && !tournament) { return <div className="text-center rakete-font my-5">Lade Turnierdaten...</div>; }
    if (!tournament && !loading) { return <div className="text-center rakete-font my-5 text-danger">Turnierdetails konnten nicht geladen werden.</div>; }
    if (!tournament) return null;

    return (
        <>
            <div className='row justify-content-center mx-3'>
                <TournamentHeader name={tournament.name} />

                <div className='col-lg-5 rounded-4 bg-white shadow-lg mt-2 mx-2 mb-3 mb-lg-0'>
                    <TournamentInfoBox
                        tournament={tournament}
                        participants={participants}
                        participantsConfirmed={participantsConfirmed}
                        participantsWait={participantsWait}
                    />
                    {tournament.completed ? (
                        <TournamentRanking ranking={tournament.ranking} />
                    ) : (
                        <TournamentActions
                            tournament={tournament}
                            onPublish={handleShowPublishConfirm}
                            onClose={() => setShowTournamentCloseModal(true)}
                            onEdit={() => setEditTournamentModal(true)}
                            onDownload={handleParticipantDownloadAction}
                            onOpenMessages={() => { setSelectedParticipant(null); setShowTournamentMessagesModal(true); }}
                            onToggleRegistration={handleToggleRegistrationAction}
                        />
                    )}
                </div>

                <div className='col-lg-6 rounded-4 bg-white shadow-sm mt-2 mx-2 py-3'>
                    <ParticipantManager
                        tournament={tournament}
                        inquiryList={inquiry}
                        confirmedList={participantsConfirmed}
                        waitList={participantsWait}
                        canceledList={participantsCanceled}
                        onShowParticipant={handleParticipantShowAction}
                        onUpdateParticipant={handleParticipantUpdateAction}
                        onDeleteParticipant={handleParticipantDeleteAction}
                        onAddParticipant={() => setAddPartificantModal(true)}
                        loading={loading}
                    />
                </div>
            </div>

            {/* Modals */}
            <TournamentEdit show={editTournamentModal} onHide={() => setEditTournamentModal(false)} tournament={tournament} refresh={fetchTournamentData} />
            <PartificantAdd show={addPartificantModal} onHide={() => setAddPartificantModal(false)} refresh={handleRefreshParticipants} tournamentid={tournament._id} />
            <ParticipantShow show={showParticipantModal} onHide={() => { setShowParticipantModal(false); setSelectedParticipant(null); }} participant={selectedParticipant} tournamentId={tournament?._id} onSendMessage={handleSendMessageRequest} onRefresh={handleRefreshParticipants} />
            <TournamentClose show={showTournamentCloseModal} onHide={() => setShowTournamentCloseModal(false)} participants={participantsConfirmed} tournamentId={tournament._id} refresh={fetchTournamentData} />
            <ConfirmModal show={showConfirmModal} onHide={handleConfirmModalHide} tournament={tournament._id} participant={selectedParticipant} topic={confirmModalTopic} />
            <TournamentMessages show={showTournamentMessagesModal} onHide={() => { setShowTournamentMessagesModal(false); setSelectedParticipant(null); }} tournament={tournament?._id} participants={participants} participant={selectedParticipant} />
            <PublishConfirmModal show={showPublishConfirmModal} onHide={() => setShowPublishConfirmModal(false)} onConfirm={handlePublicationAction} tournamentName={tournament?.name || ''} loading={loading} />
        </>
    );
}

export default Tournaments;
